import React, { useEffect, useState } from "react";
import * as style from "./UserGroups.module.scss";
import { getEnv } from "../../../Constants/env";
import Button from "../../../Components/Common/CprimeButton";
import GridTable from "cvmgmt-cube-grid-table";
import UserGroupsSidePanel from "../../../Components/UserManagment/UserGroups/UserGroupsSidePanel";
import UserGroupsPanelContent from "./UserGroupsPanelContent";
import { useDispatch, useSelector } from "react-redux";
import { resetGroupData } from "../../../store/slices/userGroupSlice";
import { GridTableDataType } from "cvmgmt-txn-history-ui-lib/types/Packages/GridTable/types";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { formatFilterValue } from "../../../utils/formatFilters";
import { triggerToast } from "../../../utils/toast";
import { getUserGroupsData } from "../../../services/RequestAccess/RequestAccess";
import { Pagination } from "cprime-foundational-components";
import { useIdToken } from "../../../hooks/useAuthSession";

interface UserGroupsProps {
  isCreateGroup?: boolean;
  appId?: number;
  adminUserGroups?: boolean;
  idToken?: string | null;
  baseURL?: string;
  env?: string;
  setCreateGroupModal?: any;
  getApplicationDetails?: () => void;
  isAccessible?: boolean;
}

const UserGroups: React.FC<UserGroupsProps> = ({
  isCreateGroup = false,
  appId,
  adminUserGroups = false,
  idToken: idTokenFromProps = null,
  baseURL: baseURLFromProps,
  env: envFromProps,
  setCreateGroupModal = () => null,
  getApplicationDetails = () => null,
  isAccessible = true,
}) => {
  const scssClass = style as any;
  const [gridData, setGridData] = useState<GridTableDataType>({
    columns: [] as GridTableDataType["columns"],
    rows: [],
    totalCount: 0,
  });
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [refresh, setRefresh] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGroupSidePanelVisible, setIsGroupSidePanelVisible] = useState(false);
  const [isEditGroup, setIsEditGroup] = useState(false);
  const [selectedUserGroup, setSelectedUserGroup] = useState(null);
  // const { env: envFromStore } = useSelector(
  //   (state: any) => state?.createApplication,
  // );

  // const baseURL = baseURLFromProps ?? getEnv()?.baseURL ?? "";
  // const env = envFromProps ?? envFromStore ?? "";
  const sessionIdToken = useIdToken();
  const idToken = idTokenFromProps ?? sessionIdToken;

  const dispatch = useDispatch();

  useEffect(() => {
    if (isCreateGroup) {
      setIsEditGroup(false);
    }
  }, [isCreateGroup]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const resp = await getUserGroupsData(
        {
          sortingFilter: {
            filter:
              filters?.length > 0
                ? (filters || [])?.map((item: any) =>
                    formatFilterValue(item?.value, item?.id),
                  )
                : [],
            sort: sorting?.map((item: any) => {
              return {
                columnName: item?.id,
                type: item?.desc ? "DESC" : "ASC",
              };
            }),
            page: {
              limit: pagination?.rowsPerPage,
              offset: pagination?.page * pagination?.rowsPerPage,
            },
          },
        },
        appId,
        adminUserGroups,
      );

      const gridResult = {
        ...resp?.data,
        columns: resp?.data?.columns?.map((column: any) => ({
          ...column,
          size: column?.size ?? 200,
          minSize: column?.minSize ?? 200,
        })),
        rows: resp?.data?.rows?.map((item: any) => {
          return {
            ...item,
            action_id: item?.action,
            action: item?.action?.map((action: any) => {
              return {
                ...action,
                disabled: isAccessible ? false : true,
              };
            }),
          };
        }),
      };
      setGridData(gridResult);
      setRefresh((perv) => perv + 1);
    } catch (error: any) {
      triggerToast(error?.message || "Unable to load publish history", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData();
    }, 300);

    return () => clearTimeout(timeout);
  }, [sorting, pagination, filters]);

  useEffect(() => {
    setPagination({
      page: 0,
      rowsPerPage: pagination?.rowsPerPage,
    });
  }, [filters]);

  return (
    <>
      <div
        style={
          !adminUserGroups && !baseURLFromProps
            ? { width: "calc(100vw - 4rem)", margin: "2rem 2rem 0 2rem" }
            : { width: "100%", margin: "0" }
        }
      >
        {!adminUserGroups ? (
          <div className={scssClass.header}>
            <div className={scssClass.title}>User Group Management</div>
            <Button
              variant="tertiary"
              size="medium"
              onClick={() => {
                setIsEditGroup(false);
                setIsGroupSidePanelVisible(true);
                dispatch(resetGroupData());
              }}
            >
              Create Group
            </Button>
          </div>
        ) : null}
        {idToken !== null && (
          <>
            <GridTable
              data={gridData}
              title={"User Groups"}
              refresh={refresh}
              style={{
                height: `calc(100vh - ${adminUserGroups ? "33.8rem" : "28rem"})`,
                maxWidth: "calc(100vw - 4rem)",
              }}
              customPayload={true}
              isCustomizeColumns={false}
              noDataMessage="No Data Found."
              onRowClick={(_, row: any) => {}}
              cellActions={[
                {
                  event: "Edit",
                  callback: (_event, data) => {
                    setSelectedUserGroup(data?.row?.original ?? null);
                    setIsEditGroup(true);
                    setIsGroupSidePanelVisible(true);
                  },
                },
              ]}
              showLoader={false}
              showHeader={true}
              filters={filters}
              sorting={sorting}
              setSorting={setSorting}
              setFilters={setFilters}
              pagination={pagination}
              loading={isLoading}
              isPagination={false}
            />
            <Pagination
              sx={{ width: "100%" }}
              {...{
                ...pagination,
                count: gridData?.totalCount ?? 0,
                rowsPerPageOptions: [25, 50, 100, 250],
                onPageChange: (_e, number) =>
                  setPagination({ ...pagination, page: number }),
                onRowsPerPageChange: (evt) =>
                  setPagination({
                    page: 0,
                    rowsPerPage: Number(evt.target.value ?? 25),
                  }),
              }}
            />
          </>
        )}
      </div>
      {(isCreateGroup || isGroupSidePanelVisible) && (
        <UserGroupsSidePanel
          appId={appId}
          open={isCreateGroup || isGroupSidePanelVisible}
          title={isEditGroup ? "Edit Group" : "Create Group"}
          isEdit={isEditGroup}
          groupId={(selectedUserGroup as any)?.id}
          adminUserGroups={adminUserGroups}
          setRefresh={() => setRefresh((prev) => prev + 1)}
          getApplicationDetails={getApplicationDetails}
          onRefreshData={fetchData}
          onClose={() => {
            if (isCreateGroup) {
              setCreateGroupModal(false);
              setIsGroupSidePanelVisible(false);
            } else {
              setIsGroupSidePanelVisible(false);
              setIsEditGroup(false);
              setSelectedUserGroup(null);
            }
          }}
          BodyComponent={
            <UserGroupsPanelContent
              isEdit={isEditGroup}
              selectedUserGroup={isEditGroup ? selectedUserGroup : {}}
              adminUserGroups={adminUserGroups}
              appId={appId}
              groupId={(selectedUserGroup as any)?.id}
            />
          }
        />
      )}
    </>
  );
};

export default UserGroups;
